import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CommonPlatform as Platform, TenantsProduct as Product, TenantsProductCreate as ProductCreate, TenantsProductUpdate as ProductUpdate, TenantsTenantKey as TenantKey } from '../generated/api/types.gen.ts';
import { productsCreate, productsList, productsRevokeKey, productsRotateKey, productsUpdate } from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';

export type { Platform, Product, ProductCreate, ProductUpdate, TenantKey };

/** Nøkkelen eies her og bare her (`react-ts-integration`, «Query-nøkler har én eier»). */
export const produktNoekkel = ['products'] as const;

/**
 * `GET /v1/admin/products` — produktene med nøkler kun som prefiks (DD-20). En kjent feil
 * opprettes på et produkt, og dette er kontraktens ene vei til id-en (M2.4).
 */
export function useProdukter() {
  const klient = useApiKlient();
  return useQuery({
    queryKey: produktNoekkel,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Product[]> => {
      const res = await productsList({ client: klient });
      if (!res.response?.ok || !res.data) throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
      return res.data as Product[];
    },
  });
}

function kast(res: { response?: Response; data?: unknown; error?: unknown }): never {
  throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
}

function useKall<TArg, TRes>(fn: (arg: TArg) => Promise<{ response?: Response; data?: unknown; error?: unknown }>, etter: () => void) {
  return useMutation({
    mutationFn: async (arg: TArg): Promise<TRes> => {
      const res = await fn(arg);
      if (!res.response?.ok) kast(res);
      return res.data as TRes;
    },
    onSuccess: etter,
  });
}

/** Opprett: produktet kommer med sin første SDK-nøkkel i klartekst — den ene gangen (DD-20). Kun eier. */
export function useOpprettProdukt() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useKall<ProductCreate, Product>(
    (body) => productsCreate({ client: klient, body }),
    () => void queryClient.invalidateQueries({ queryKey: produktNoekkel }),
  );
}

/** Navn, plattformer, bundle-ID-er og origins — aldri nøklene. Kun eier. */
export function useOppdaterProdukt() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useKall<{ id: string; body: ProductUpdate }, Product>(
    ({ id, body }) => productsUpdate({ client: klient, path: { id }, body }),
    () => void queryClient.invalidateQueries({ queryKey: produktNoekkel }),
  );
}

/** Roter: ny nøkkel ved siden av den gamle, klartekst kun her. Maks to aktive — 409 ellers. Kun eier. */
export function useRoterNoekkel() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useKall<string, TenantKey>(
    (id) => productsRotateKey({ client: klient, path: { id } }),
    () => void queryClient.invalidateQueries({ queryKey: produktNoekkel }),
  );
}

/** Kall tilbake: 401 fra neste kall. Idempotent. Kun eier. */
export function useKallTilbakeNoekkel() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useKall<{ id: string; keyId: string }, void>(
    ({ id, keyId }) => productsRevokeKey({ client: klient, path: { id, keyId } }),
    () => void queryClient.invalidateQueries({ queryKey: produktNoekkel }),
  );
}
